/**
 * Split auth layout shared by /login and /registration, with the brand panel
 * sliding between the two sides instead of being torn down and rebuilt.
 *
 * See AuthSplitLayout.styles.ts for the three-slot track geometry. This file
 * owns what the CSS cannot: keeping the outgoing form mounted for the slide,
 * agreeing with the CSS on when the split collapses, and moving focus to the
 * incoming form.
 */

import BrandLogo from "@funded-here-interface/common/src/assets/logo2.png";
import ContentLoader from "@funded-here-interface/common/src/components/ContentLoader/ContentLoader";
import { FUNDEDHERE_WEBSITE_URL } from "@funded-here-interface/common/src/constant/constant";
import {
  CSSProperties,
  FC,
  ReactNode,
  Suspense,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";

import { SLIDE_DURATION_MS, useStyles } from "./AuthSplitLayout.styles";
import { useContainerCollapsed } from "./useContainerCollapsed";

/** Which half of the split the routed form occupies. */
export type FormSide = "left" | "right";

interface AuthSplitLayoutProps {
  /** The routed screen's form content. */
  content: ReactNode;
  /**
   * `right` for Login ([brand | form]), `left` for Registration
   * ([form | brand]). Driving the slide off this rather than off the pathname
   * keeps the layout ignorant of the routes that use it.
   */
  side: FormSide;
  /**
   * Accessible name for the form panel ("Log in" / "Sign up"). The panel is a
   * `role="group"`, and an unnamed group is announced as just "group".
   */
  formLabel: string;
  /**
   * The OTHER auth screen's `import()` factory (login passes registration's and
   * vice versa). Called once on mount to warm that chunk in the background, so
   * the first Sign up / Log in click slides a real form across instead of a
   * loader. Bundler `import()` promises are cached, so this costs one fetch and
   * every later call is free.
   *
   * Optional: without it the layout still works, the first navigation just
   * shows the Suspense loader in the form column while the chunk downloads.
   */
  preloadOther?: () => Promise<unknown>;
}

interface Snapshot {
  node: ReactNode;
  side: FormSide;
  label: string;
}

interface SlotContent {
  node: ReactNode;
  label: string;
}

const AuthSplitLayout: FC<AuthSplitLayoutProps> = ({ content, side, formLabel, preloadOther }) => {
  const { classes, cx } = useStyles();

  // Agrees with the `@container` rules on when the split collapses — see
  // useContainerCollapsed.ts for why a viewport media query cannot.
  const [containerRef, isCollapsed] = useContainerCollapsed<HTMLDivElement>();

  // What is on screen, and what is sliding away (non-null only mid-slide).
  const [shown, setShown] = useState<Snapshot>({ node: content, side, label: formLabel });
  const [outgoing, setOutgoing] = useState<Snapshot | null>(null);

  // Adjusted during render, not in an effect, so React re-renders with the new
  // `shown`/`outgoing` before committing. An effect runs after the commit, by
  // which point the outgoing form has been unmounted along with its typed
  // input and Turnstile widget; `useLayoutEffect` is no earlier. Same type at
  // the same tree position (`formPanel` > `Suspense` > form) keeps the instance.
  if (shown.side !== side) {
    // Collapsed: there is no slide, so swap sides without staging a form.
    setOutgoing(isCollapsed ? null : shown);
    setShown({ node: content, side, label: formLabel });
  }

  // Un-stage the outgoing form once its slide finishes. Keyed on `outgoing`'s
  // identity, so a second toggle mid-slide restarts the timer.
  useEffect(() => {
    if (!outgoing) return undefined;
    const timer = window.setTimeout(() => setOutgoing(null), SLIDE_DURATION_MS);
    return () => window.clearTimeout(timer);
  }, [outgoing]);

  // Fetch the sibling screen's chunk in the background, once, so the slide has
  // a real form to carry the first time the user switches. Deferred to idle
  // (or a tick, where requestIdleCallback is unavailable — Safari) so it never
  // competes with painting the screen the user actually asked for. A failed
  // prefetch is deliberately swallowed: the Suspense boundary above will fetch
  // it again for real on navigation and surface any genuine error there.
  useEffect(() => {
    if (!preloadOther) return undefined;

    const warm = () => {
      // Warms the bundler's cached `import()` promise. It doesn't prime
      // `lazy()`, but App.tsx's `lazy()` reads the same promise, so the first
      // navigation finds the chunk already downloaded.
      void preloadOther().catch(() => {});
    };

    if (typeof window.requestIdleCallback === "function") {
      const handle = window.requestIdleCallback(warm, { timeout: 2000 });
      return () => window.cancelIdleCallback(handle);
    }
    const handle = window.setTimeout(warm, 300);
    return () => window.clearTimeout(handle);
  }, [preloadOther]);

  // Focus the incoming form panel before paint. Keyed on `side` through a ref,
  // not on `outgoing`, so collapsed mode (no outgoing form) is covered too;
  // the ref starts at the first `side` so mounting doesn't steal focus.
  const prevSideRef = useRef<FormSide>(side);
  const incomingFormPanelRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (prevSideRef.current === side) return;
    prevSideRef.current = side;
    // The slide brings the panel into view; a focus scroll would fight it.
    incomingFormPanelRef.current?.focus({ preventScroll: true });
  }, [side]);

  // Each side shows the live content when the route is on that side, and the
  // outgoing snapshot when it is sliding off it. Everything else is an empty
  // slot, so the form that is neither current nor leaving stays UNMOUNTED —
  // this is what keeps Registration's Turnstile widget from initialising
  // while the user is on the login page.
  const slotFor = (target: FormSide): SlotContent | null => {
    if (side === target) return { node: content, label: formLabel };
    // Re-checked here: collapsing mid-slide must drop the staged form at once.
    if (!isCollapsed && outgoing?.side === target)
      return { node: outgoing.node, label: outgoing.label };
    return null;
  };

  // One slot = one third of the track. Registration (form left) shows slots
  // 0-1 at rest; Login (form right) shows slots 1-2, one slot further along.
  //
  // Passed as a CUSTOM PROPERTY, never as `transform` itself. An inline
  // `transform` outranks every class rule, so the track's own
  // `@container (max-width: 820px) { transform: none }` could not undo it —
  // below the split breakpoint the single-column track stayed shifted a third
  // of the viewport to the left, taking the whole login/signup form off the
  // left edge of the screen with it. Handing CSS the offset as data and
  // letting the stylesheet decide whether to apply it is also what CLAUDE.md's
  // "layout is CSS-only" rule asks for.
  const trackOffset = side === "left" ? "translateX(0)" : "translateX(-33.3333%)";

  // The Suspense boundary belongs HERE, not up at App.tsx's — same rule the
  // other layouts follow. Both screens are lazy(), so the first navigation to
  // one fetches its chunk and suspends; caught by the outer boundary that
  // swaps out the whole route element, the brand panel would be unmounted and
  // rebuilt and the slide would not run at all (which is why the animation
  // only appeared from the SECOND visit onward, once the chunk was cached).
  // Catching it inside the slot keeps the panel mounted and confines the
  // loader to the form column. Suspense does not catch errors, and the
  // investor app has no error boundary (a pre-existing gap).
  const renderFormSlot = (target: FormSide) => {
    const slotContent = slotFor(target);
    const isIncoming = target === side;
    const isOutgoing = slotContent !== null && !isIncoming;

    return (
      <div
        className={cx(classes.slot, !slotContent && classes.slotEmpty)}
        // `|| undefined`: React renders `aria-hidden={false}` as a literal
        // `aria-hidden="false"`, and only empty slots should carry it.
        aria-hidden={!slotContent || undefined}
        // `inert`, not `aria-hidden`: the outgoing form still holds focus
        // during the slide, and `aria-hidden` on a focused element's ancestor
        // trips Chrome's "Blocked aria-hidden" warning. `inert` also blocks input.
        inert={isOutgoing}
      >
        {slotContent ? (
          <div
            ref={isIncoming ? incomingFormPanelRef : undefined}
            className={classes.formPanel}
            // Not `<fieldset>`: this wraps a whole screen, not a set of related
            // controls, and fieldset adds a border and form-grouping semantics.
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
            role="group"
            aria-label={slotContent.label}
            // A focus target for the slide handoff, not a Tab stop.
            tabIndex={-1}
          >
            <Suspense fallback={<ContentLoader />}>{slotContent.node}</Suspense>
          </div>
        ) : null}
      </div>
    );
  };

  return (
    <div className={classes.parentContainer} ref={containerRef} data-split-collapsed={isCollapsed}>
      <div className={classes.viewport}>
        <div className={classes.track} style={{ "--track-offset": trackOffset } as CSSProperties}>
          {renderFormSlot("left")}

          <div className={cx(classes.slot, classes.brandSlot)}>
            <div className={classes.brandPanel}>
              <div className={classes.brandImage} />

              {/* Opens in a new tab so an in-progress login/signup isn't
                  navigated away. */}
              <a
                className={classes.brandLogoLink}
                href={FUNDEDHERE_WEBSITE_URL}
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Go to the FundedHere website"
              >
                <img src={BrandLogo} className={classes.brandLogo} alt="FundedHere" />
              </a>
            </div>
          </div>

          {renderFormSlot("right")}
        </div>
      </div>
    </div>
  );
};

export default AuthSplitLayout;
